import React, { useState } from 'react';
import { Upload, Button, List, message, Modal, Tag, Space } from 'antd';
import { UploadOutlined, DeleteOutlined, FileOutlined, FilePdfOutlined, FileImageOutlined, FileWordOutlined, FileExcelOutlined, CloudUploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import type { Attachment } from '../../services/knowledgeBase/knowledgeBaseService';
import { resolveUploadAssetUrl } from '../../utils/resolveUploadAssetUrl';

interface AttachmentUploaderProps {
  value?: Attachment[];
  onChange?: (attachments: Attachment[]) => void;
  maxSize?: number; // 最大文件大小（MB），默认100MB
  maxCount?: number; // 最大文件数量，默认10
  /** 实验记录本等：大号拖拽区 + 说明文案 */
  variant?: 'default' | 'curator';
}

const ACCEPT_DEFAULT =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.webp,.zip,.rar,.7z';
const ACCEPT_CURATOR = `${ACCEPT_DEFAULT},.csv`;

const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  value = [],
  onChange,
  maxSize = 100,
  maxCount = 10,
  variant = 'default'
}) => {
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // 获取文件图标
  const getFileIcon = (type: string, name: string) => {
    if (type === 'image') {
      return <FileImageOutlined style={{ color: '#52c41a' }} />;
    }
    const ext = name.toLowerCase().substring(name.lastIndexOf('.'));
    if (ext === '.pdf') {
      return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    } else if (['.doc', '.docx'].includes(ext)) {
      return <FileWordOutlined style={{ color: '#1890ff' }} />;
    } else if (['.xls', '.xlsx'].includes(ext)) {
      return <FileExcelOutlined style={{ color: '#52c41a' }} />;
    }
    return <FileOutlined />;
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // 处理文件上传
  const handleUpload = async (file: File) => {
    // 验证文件大小
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      message.error(`文件大小不能超过 ${maxSize}MB`);
      return false;
    }

    // 验证文件数量
    if (value.length >= maxCount) {
      message.error(`最多只能上传 ${maxCount} 个文件`);
      return false;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('attachments', file);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/attachment-upload', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      const result = await response.json();

      if (result.success && result.data.attachments && result.data.attachments.length > 0) {
        const newAttachment = result.data.attachments[0];
        const updatedAttachments = [...value, newAttachment];
        onChange?.(updatedAttachments);
        message.success('附件上传成功');
      } else {
        throw new Error(result.message || '上传失败');
      }

      return false; // 阻止默认上传
    } catch (error: any) {
      console.error('上传失败:', error);
      message.error(error.message || '上传失败，请重试');
      return false;
    } finally {
      setUploading(false);
    }
  };

  // 删除附件
  const handleDelete = (index: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个附件吗？',
      onOk: () => {
        const updatedAttachments = value.filter((_, i) => i !== index);
        onChange?.(updatedAttachments);
        message.success('附件已删除');
      },
    });
  };

  const accept = variant === 'curator' ? ACCEPT_CURATOR : ACCEPT_DEFAULT;
  const uploadProps = {
    beforeUpload: (file: File) => {
      void handleUpload(file);
      return false as const;
    },
    accept,
    showUploadList: false as const,
    disabled: uploading || value.length >= maxCount
  };

  return (
    <div className={variant === 'curator' ? 'er-curator-upload-root' : undefined}>
      {variant === 'curator' ? (
        <Upload.Dragger {...uploadProps} className="er-curator-dragger">
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined style={{ color: 'var(--er-primary, #00355f)', fontSize: 48 }} />
          </p>
          <p className="ant-upload-text" style={{ fontWeight: 700, color: 'var(--er-on, #191c1d)' }}>
            拖拽文件到此处
          </p>
          <p className="ant-upload-hint" style={{ color: 'var(--er-on-variant, #42474f)', fontSize: 13 }}>
            支持 CSV、XLSX、PNG、PDF 等，单文件最大 {maxSize}MB
          </p>
          <Button type="default" disabled={uploading || value.length >= maxCount} style={{ marginTop: 8 }}>
            浏览本地文件
          </Button>
        </Upload.Dragger>
      ) : (
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />} disabled={uploading || value.length >= maxCount}>
            上传附件
          </Button>
        </Upload>
      )}

      {value.length > 0 && (
        <List
          className={variant === 'curator' ? 'er-curator-file-list' : undefined}
          style={{ marginTop: variant === 'curator' ? 20 : 16 }}
          dataSource={value}
          renderItem={(attachment, index) => (
            <List.Item
              className={variant === 'curator' ? 'er-curator-file-item' : undefined}
              actions={[
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(index)}
                  disabled={uploading}
                >
                  删除
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={getFileIcon(attachment.type, attachment.name)}
                title={
                  <Space>
                    <a
                      href={resolveUploadAssetUrl(attachment.url) || attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {attachment.name}
                    </a>
                    <Tag color={attachment.type === 'image' ? 'green' : attachment.type === 'document' ? 'blue' : 'default'}>
                      {attachment.type === 'image' ? '图片' : attachment.type === 'document' ? '文档' : '其他'}
                    </Tag>
                  </Space>
                }
                description={formatFileSize(attachment.size)}
              />
            </List.Item>
          )}
        />
      )}

      {variant === 'default' ? (
        <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
          支持格式：PDF, Word, Excel, PPT, 图片, ZIP等 | 最大大小：{maxSize}MB | 最多{maxCount}个文件
          {value.length > 0 && ` (已上传 ${value.length}/${maxCount})`}
        </div>
      ) : (
        <p className="er-curator-upload-meta">
          最多 {maxCount} 个文件 · 已上传 {value.length}/{maxCount}
        </p>
      )}
    </div>
  );
};

export default AttachmentUploader;



